import { useQuery } from '@tanstack/react-query';
import { Users, Layers, UserCircle, Calendar, Plus, Upload, Dumbbell } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useLoadPreferences } from '../../hooks/useLoadPreferences';
import { Header, Card, Button, EmptyState } from '../../components/ui';
import { athletesApi } from '../../api/athletes';
import { groupsApi } from '../../api/groups';
import { coachesApi } from '../../api/coaches';
import { eventsApi } from '../../api/events';
import { assistantsApi } from '../../api/assistants';
import { useCurrentOrganization } from '../../hooks/useCurrentOrganization';
import { useSubscriptionLimits } from '../../hooks/useSubscriptionLimits';
import { formatEventDateShort, EVENT_TYPE_ICONS } from '../../types/event';
import type { Event as AppEvent } from '../../types/event';

// -- Helpers ------------------------------------------------------------------

// Цвета для виджетов сетки (Orange Drive Palette)
const WIDGET_COLORS = {
  athletes: '#F57C00', // var(--avatar-1)
  groups: '#E65100',   // var(--avatar-2)
  coaches: '#FB8C00',  // var(--avatar-3)
  events: '#FF9800',   // var(--avatar-4)
} as const;

// -- Widget ------------------------------------------------------------------

interface StatWidgetProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  limit?: number; // -1 = unlimited
  color: string;
  onClick: () => void;
}

const StatWidget: React.FC<StatWidgetProps> = ({
  icon,
  label,
  count,
  limit,
  color,
  onClick,
}) => {
  const limitText =
    limit !== undefined && limit !== -1 ? `/${limit}` : '';

  return (
    <Card className="flex flex-col items-center justify-center gap-1 py-4" onClick={onClick}>
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-1"
        style={{ backgroundColor: `${color}18` }}
      >
        {icon}
      </div>
      <span
        className="text-2xl font-bold"
        style={{ color: 'var(--text-primary)' }}
      >
        {count}
        {limitText && (
          <span className="text-sm font-normal" style={{ color: 'var(--text-hint)' }}>
            {limitText}
          </span>
        )}
      </span>
      <span
        className="text-[13px]"
        style={{ color: 'var(--text-secondary)' }}
      >
        {label}
      </span>
    </Card>
  );
};

// -- Upcoming Event card -----------------------------------------------------

interface UpcomingEventCardProps {
  event: AppEvent;
  onClick: () => void;
}

const UpcomingEventCard: React.FC<UpcomingEventCardProps> = ({ event, onClick }) => {
  const icon = EVENT_TYPE_ICONS[event.event_type] || EVENT_TYPE_ICONS.other;

  return (
    <Card onClick={onClick}>
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
          style={{ backgroundColor: 'var(--bg-tertiary, var(--bg-secondary))' }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[15px] font-semibold truncate"
            style={{ color: 'var(--text-primary)' }}
          >
            {event.name}
          </div>
          <div
            className="text-[13px] truncate"
            style={{ color: 'var(--text-hint)' }}
          >
            {formatEventDateShort(event.start_date)}
            {event.location ? ` \u00B7 ${event.location}` : ''}
          </div>
        </div>
      </div>
    </Card>
  );
};

// -- Loading skeleton --------------------------------------------------------

const Skeleton: React.FC = () => (
  <div className="animate-pulse space-y-4 px-4 pt-6">
    {/* header */}
    <div className="h-8 rounded w-40" style={{ backgroundColor: 'var(--bg-secondary)' }} />
    <div className="h-4 rounded w-24" style={{ backgroundColor: 'var(--bg-secondary)' }} />
    {/* grid */}
    <div className="grid grid-cols-2 gap-3 mt-6">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-28 rounded-xl"
          style={{ backgroundColor: 'var(--bg-secondary)' }}
        />
      ))}
    </div>
  </div>
);

// -- Main component ----------------------------------------------------------

export function HomeScreen() {
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const { switchTab, pushScreen, openDirectionSwitcher } = useTabNavigationStore();
  const { isLoading: isLoadingPreferences } = useLoadPreferences();

  const { data: organization } = useCurrentOrganization();
  const {
    maxAthletes,
    maxGroups,
    maxCoaches,
    isLoading: isLoadingLimits,
  } = useSubscriptionLimits();

  // -- Data queries ----------------------------------------------------------

  const { data: assistant, isLoading: isLoadingAssistant } = useQuery({
    queryKey: ['assistant', currentAssistantId],
    queryFn: () => assistantsApi.getById(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const { data: athletes, isLoading: isLoadingAthletes } = useQuery({
    queryKey: ['athletes', currentAssistantId],
    queryFn: () => athletesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const { data: groups, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['groups', currentAssistantId],
    queryFn: () => groupsApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const { data: coaches, isLoading: isLoadingCoaches } = useQuery({
    queryKey: ['coaches', currentAssistantId],
    queryFn: () => coachesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const { data: upcomingEvents } = useQuery({
    queryKey: ['events-upcoming', currentAssistantId],
    queryFn: () => eventsApi.getUpcoming(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  // -- Derived state ---------------------------------------------------------

  const totalAthletes = athletes?.length ?? 0;
  const totalGroups = groups?.length ?? 0;
  const totalCoaches = coaches?.length ?? 0;
  const totalEvents = upcomingEvents?.length ?? 0;

  const isLoading =
    isLoadingAssistant || isLoadingAthletes || isLoadingGroups || isLoadingCoaches || isLoadingLimits || isLoadingPreferences;

  const nextEvent = upcomingEvents?.[0] ?? null;

  // -- Loading (including preferences) ---------------------------------------

  if (isLoading) {
    return <Skeleton />;
  }

  // -- No assistant selected (onboarding) ------------------------------------
  // Only show after preferences loaded to avoid flashing this screen

  if (!currentAssistantId) {
    return (
      <EmptyState
        icon={Dumbbell}
        title="Создайте первое направление"
        description="Направление -- это вид спорта или активности, которым вы занимаетесь. Например, Футбол или Йога."
        primaryAction={{
          label: 'Создать направление',
          onClick: () => openDirectionSwitcher(),
        }}
      />
    );
  }

  // -- Subtitle (organization name if user has org) --------------------------

  const subtitle = organization?.name ?? undefined;

  // -- Render ----------------------------------------------------------------

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="px-4 pt-4">
        {/* Header */}
        <Header
          title={assistant?.name ?? 'Главная'}
          subtitle={subtitle}
        />

        {/* Stat Widgets 2x2 */}
        <div className="grid grid-cols-2 gap-3">
          <StatWidget
            icon={<Users size={22} color={WIDGET_COLORS.athletes} />}
            label="Спортсмены"
            count={totalAthletes}
            limit={maxAthletes}
            color={WIDGET_COLORS.athletes}
            onClick={() => switchTab('athletes')}
          />
          <StatWidget
            icon={<Layers size={22} color={WIDGET_COLORS.groups} />}
            label="Группы"
            count={totalGroups}
            limit={maxGroups}
            color={WIDGET_COLORS.groups}
            onClick={() => switchTab('groups')}
          />
          <StatWidget
            icon={<UserCircle size={22} color={WIDGET_COLORS.coaches} />}
            label="Тренеры"
            count={totalCoaches}
            limit={maxCoaches}
            color={WIDGET_COLORS.coaches}
            onClick={() => {
              switchTab('groups');
              pushScreen('coaches');
            }}
          />
          <StatWidget
            icon={<Calendar size={22} color={WIDGET_COLORS.events} />}
            label="События"
            count={totalEvents}
            color={WIDGET_COLORS.events}
            onClick={() => pushScreen('events')}
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mt-4">
          <Button
            variant="secondary"
            onClick={() => {
              switchTab('athletes');
              // TODO: auto-open create form after tab switch
            }}
            className="flex-1 !h-10 !text-[15px]"
          >
            <Plus size={16} className="mr-1" />
            Спортсмен
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              switchTab('groups');
              // TODO: auto-open create form after tab switch
            }}
            className="flex-1 !h-10 !text-[15px]"
          >
            <Plus size={16} className="mr-1" />
            Группа
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              switchTab('athletes');
              pushScreen('import-athletes', { assistantId: currentAssistantId });
            }}
            className="!h-10 !text-[15px] !px-3"
          >
            <Upload size={16} />
          </Button>
        </div>

        {/* Upcoming Event */}
        {nextEvent && (
          <div className="mt-6">
            <h2
              className="text-[15px] font-semibold mb-3"
              style={{ color: 'var(--text-secondary)' }}
            >
              Ближайшее событие
            </h2>
            <UpcomingEventCard
              event={nextEvent}
              onClick={() =>
                pushScreen('event-detail', { eventId: nextEvent.id })
              }
            />
          </div>
        )}

        {/* Empty hint when nothing yet */}
        {totalAthletes === 0 && totalGroups === 0 && totalCoaches === 0 && (
          <div className="mt-8 text-center">
            <p
              className="text-[15px]"
              style={{ color: 'var(--text-hint)' }}
            >
              Добавьте спортсменов и группы, чтобы начать работу
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
